import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import { 
  Clock, Plus, Save, Trash2, Edit2, DollarSign, Calendar,
  Sun, Moon, Star, Zap, AlertTriangle, Award, Gem, Heart,
  Coffee, Briefcase, Users, Home, Sparkles, TrendingUp
} from 'lucide-react';

interface WorkPattern {
  id?: number;
  name: string;
  description: string;
  start_time1: string;
  end_time1: string;
  start_time2?: string | null;
  end_time2?: string | null;
  break_required: boolean;
  is_active: boolean;
  background_color?: string;
}

interface WorkPatternManagerProps {
  onPatternsUpdated?: () => void;
}

interface WageRule {
  id?: number;
  name: string;
  type: 'weekend' | 'holiday' | 'time' | 'peak';
  amount: number;
  conditions: {
    days?: string[];
    start_time?: string;
    end_time?: string;
    dates?: string[];
  };
  icon?: string;
  icon_color?: string;
  is_active: boolean;
}

const WorkPatternManager: React.FC<WorkPatternManagerProps> = ({ onPatternsUpdated }) => {
  const [workPatterns, setWorkPatterns] = useState<WorkPattern[]>([]);
  const [wageRules, setWageRules] = useState<WageRule[]>([]);
  const [editingPattern, setEditingPattern] = useState<WorkPattern | null>(null);
  const [editingRule, setEditingRule] = useState<WageRule | null>(null);
  const [activeTab, setActiveTab] = useState<'patterns' | 'rules'>('patterns');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // アイコンコンポーネントを取得
  const getIconComponent = (iconValue: string | undefined) => {
    if (!iconValue) return null;
    const iconOption = iconOptions.find(opt => opt.value === iconValue);
    return iconOption?.icon || null;
  };

  // アイコンの色クラスを取得
  const getIconColorClass = (colorValue: string | undefined, iconValue: string | undefined) => {
    if (colorValue) return colorValue;
    const iconOption = iconOptions.find(opt => opt.value === iconValue);
    return iconOption?.defaultColor || 'text-gray-500';
  };

  // 色のクラスを明示的に定義（Tailwindのパージ問題を回避）
  const getColorClasses = (colorValue: string | undefined) => {
    if (!colorValue) return 'bg-gray-100 text-gray-800';
    
    // 色のマッピングを明示的に定義
    const colorMap: { [key: string]: string } = {
      'bg-green-100 text-green-800': 'bg-green-100 text-green-800',
      'bg-blue-100 text-blue-800': 'bg-blue-100 text-blue-800',
      'bg-purple-100 text-purple-800': 'bg-purple-100 text-purple-800',
      'bg-yellow-100 text-yellow-800': 'bg-yellow-100 text-yellow-800',
      'bg-orange-100 text-orange-800': 'bg-orange-100 text-orange-800',
      'bg-indigo-100 text-indigo-800': 'bg-indigo-100 text-indigo-800',
      'bg-pink-100 text-pink-800': 'bg-pink-100 text-pink-800',
      'bg-gray-100 text-gray-800': 'bg-gray-100 text-gray-800',
      'bg-red-100 text-red-800': 'bg-red-100 text-red-800',
      'bg-teal-100 text-teal-800': 'bg-teal-100 text-teal-800'
    };
    
    return colorMap[colorValue] || 'bg-gray-100 text-gray-800';
  };

  // 標準的な勤務パターン
  const defaultPatterns: WorkPattern[] = [
    {
      name: '通常シフト（分割）',
      description: '10:00-14:00 & 17:00-21:00',
      start_time1: '10:00',
      end_time1: '14:00',
      start_time2: '17:00',
      end_time2: '21:00',
      break_required: false,
      is_active: true,
      background_color: 'bg-green-100 text-green-800'
    },
    {
      name: 'フルタイム（中抜けあり）',
      description: '10:00-21:00（中抜け14:00-17:00）',
      start_time1: '10:00',
      end_time1: '21:00',
      start_time2: null,
      end_time2: null,
      break_required: true,
      is_active: true,
      background_color: 'bg-blue-100 text-blue-800'
    },
    {
      name: 'オーナー勤務',
      description: '10:00-21:00（中抜けなし）',
      start_time1: '10:00',
      end_time1: '21:00',
      start_time2: null,
      end_time2: null,
      break_required: false,
      is_active: true,
      background_color: 'bg-indigo-100 text-indigo-800'
    },
    {
      name: '繁忙期シフト',
      description: '10:00-15:00 & 16:00-21:00',
      start_time1: '10:00',
      end_time1: '15:00',
      start_time2: '16:00',
      end_time2: '21:00',
      break_required: false,
      is_active: true,
      background_color: 'bg-orange-100 text-orange-800'
    }
  ];

  // 利用可能なアイコンオプション
  const iconOptions = [
    { value: 'sun', label: '太陽', icon: Sun, defaultColor: 'text-yellow-500' },
    { value: 'moon', label: '月', icon: Moon, defaultColor: 'text-blue-500' },
    { value: 'star', label: '星', icon: Star, defaultColor: 'text-yellow-400' },
    { value: 'zap', label: '稲妻', icon: Zap, defaultColor: 'text-purple-500' },
    { value: 'alert', label: '警告', icon: AlertTriangle, defaultColor: 'text-orange-500' },
    { value: 'award', label: '賞', icon: Award, defaultColor: 'text-green-500' },
    { value: 'gem', label: '宝石', icon: Gem, defaultColor: 'text-cyan-500' },
    { value: 'heart', label: 'ハート', icon: Heart, defaultColor: 'text-red-500' },
    { value: 'coffee', label: 'コーヒー', icon: Coffee, defaultColor: 'text-amber-600' },
    { value: 'briefcase', label: 'ビジネス', icon: Briefcase, defaultColor: 'text-gray-600' },
    { value: 'users', label: 'グループ', icon: Users, defaultColor: 'text-indigo-500' },
    { value: 'sparkles', label: 'キラキラ', icon: Sparkles, defaultColor: 'text-pink-500' },
    { value: 'trend', label: 'トレンド', icon: TrendingUp, defaultColor: 'text-teal-500' },
    { value: 'dollar', label: 'お金', icon: DollarSign, defaultColor: 'text-green-600' }
  ];

  // 利用可能な背景色オプション
  const colorOptions = [
    { value: 'bg-green-100 text-green-800', label: '緑', preview: 'bg-green-100' },
    { value: 'bg-blue-100 text-blue-800', label: '青', preview: 'bg-blue-100' },
    { value: 'bg-purple-100 text-purple-800', label: '紫', preview: 'bg-purple-100' },
    { value: 'bg-yellow-100 text-yellow-800', label: '黄', preview: 'bg-yellow-100' },
    { value: 'bg-orange-100 text-orange-800', label: 'オレンジ', preview: 'bg-orange-100' },
    { value: 'bg-indigo-100 text-indigo-800', label: '藍色', preview: 'bg-indigo-100' },
    { value: 'bg-pink-100 text-pink-800', label: 'ピンク', preview: 'bg-pink-100' },
    { value: 'bg-gray-100 text-gray-800', label: 'グレー', preview: 'bg-gray-100' },
    { value: 'bg-red-100 text-red-800', label: '赤', preview: 'bg-red-100' },
    { value: 'bg-teal-100 text-teal-800', label: 'ティール', preview: 'bg-teal-100' }
  ];

  // デフォルトの時給変動ルール
  const defaultRules: WageRule[] = [
    {
      name: '土日祝手当',
      type: 'weekend',
      amount: 100,
      conditions: { days: ['土', '日', '祝'] },
      is_active: true
    },
    {
      name: '夜間手当',
      type: 'time',
      amount: 50,
      conditions: { start_time: '18:00', end_time: '21:00' },
      is_active: true
    },
    {
      name: '繁忙期手当',
      type: 'peak',
      amount: 200,
      conditions: { dates: ['12/29', '12/30', '12/31', '1/1', '1/2', '1/3'] },
      is_active: true
    }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 勤務パターンを取得（データベースのデータのみ使用）
      const patternsRes = await apiClient.get('/work-patterns');
      setWorkPatterns(patternsRes.data);
      
      // 時給変動ルールを取得（データベースのデータのみ使用）
      const rulesRes = await apiClient.get('/wage-rules');
      setWageRules(rulesRes.data);
    } catch (error) {
      console.error('データ取得エラー:', error);
      setMessage({ type: 'error', text: 'データの取得に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const savePattern = async () => {
    if (!editingPattern) return;
    
    setLoading(true);
    try {
      if (editingPattern.id) {
        await apiClient.put(`/work-patterns/${editingPattern.id}`, editingPattern);
      } else {
        await apiClient.post('/work-patterns', editingPattern);
      }
      setMessage({ type: 'success', text: '勤務パターンを保存しました' });
      fetchData();
      setEditingPattern(null);
      // 親コンポーネントに更新を通知
      if (onPatternsUpdated) {
        onPatternsUpdated();
      }
    } catch (error) {
      setMessage({ type: 'error', text: '保存に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const deletePattern = async (id: number) => {
    if (!confirm('このパターンを削除しますか？')) return;
    
    try {
      await apiClient.delete(`/work-patterns/${id}`);
      setMessage({ type: 'success', text: 'パターンを削除しました' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: '削除に失敗しました' });
    }
  };

  const saveRule = async () => {
    if (!editingRule) return;
    
    setLoading(true);
    try {
      if (editingRule.id) {
        await apiClient.put(`/wage-rules/${editingRule.id}`, editingRule);
      } else {
        await apiClient.post('/wage-rules', editingRule);
      }
      setMessage({ type: 'success', text: '時給ルールを保存しました' });
      fetchData();
      setEditingRule(null);
    } catch (error) {
      setMessage({ type: 'error', text: '保存に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const deleteRule = async (id: number) => {
    if (!confirm('このルールを削除しますか？')) return;
    
    try {
      await apiClient.delete(`/wage-rules/${id}`);
      setMessage({ type: 'success', text: 'ルールを削除しました' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: '削除に失敗しました' });
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">勤務パターン・時給設定</h2>

      {/* タブ切り替え */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('patterns')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'patterns'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <Clock className="inline-block w-4 h-4 mr-2" />
          勤務パターン
        </button>
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'rules'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <DollarSign className="inline-block w-4 h-4 mr-2" />
          時給変動ルール
        </button>
      </div>

      {/* メッセージ表示 */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 勤務パターンタブ */}
      {activeTab === 'patterns' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">勤務パターン一覧</h3>
            <button
              onClick={() => setEditingPattern({
                name: '',
                description: '',
                start_time1: '10:00',
                end_time1: '14:00',
                start_time2: null,
                end_time2: null,
                break_required: false,
                is_active: true,
                background_color: 'bg-gray-100 text-gray-800'
              })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              新規追加
            </button>
          </div>

          <div className="space-y-3">
            {workPatterns.map((pattern, index) => (
              <div key={pattern.id || index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">{pattern.name}</h4>
                      {pattern.background_color && (
                        <span className={`px-2 py-1 rounded text-xs ${getColorClasses(pattern.background_color)}`}>
                          プレビュー
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{pattern.description}</p>
                    <div className="mt-2 text-sm">
                      <span className="font-medium">時間: </span>
                      {pattern.start_time1} - {pattern.end_time1}
                      {pattern.start_time2 && pattern.end_time2 && (
                        <span> & {pattern.start_time2} - {pattern.end_time2}</span>
                      )}
                      {pattern.break_required && (
                        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                          中抜け必須
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingPattern(pattern)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {pattern.id && (
                      <button
                        onClick={() => deletePattern(pattern.id!)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 時給変動ルールタブ */}
      {activeTab === 'rules' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">時給変動ルール一覧</h3>
            <button
              onClick={() => setEditingRule({
                name: '',
                type: 'weekend',
                amount: 100,
                conditions: {},
                is_active: true
              })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              新規追加
            </button>
          </div>

          <div className="space-y-3">
            {wageRules.map((rule, index) => (
              <div key={rule.id || index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    {/* アイコン表示 */}
                    {rule.icon && (() => {
                      const IconComponent = getIconComponent(rule.icon);
                      return IconComponent ? (
                        <div className={`mt-1 ${getIconColorClass(rule.icon_color, rule.icon)}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                      ) : null;
                    })()}
                    <div>
                      <h4 className="font-semibold">{rule.name}</h4>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">増額: </span>
                        <span className="text-green-600 font-bold">+{rule.amount}円</span>
                        <span className="ml-4 font-medium">条件: </span>
                        {rule.type === 'weekend' && `${rule.conditions.days?.join('・')}`}
                        {rule.type === 'time' && `${rule.conditions.start_time}〜${rule.conditions.end_time}`}
                        {rule.type === 'peak' && `繁忙期`}
                        {rule.type === 'holiday' && `祝日`}
                      </div>
                    </div>
                    {!rule.is_active && (
                      <span className="inline-block mt-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        無効
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingRule(rule)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {rule.id && (
                      <button
                        onClick={() => deleteRule(rule.id!)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 編集モーダル - 勤務パターン */}
      {editingPattern && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              {editingPattern.id ? 'パターン編集' : 'パターン追加'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">パターン名</label>
                <input
                  type="text"
                  value={editingPattern.name}
                  onChange={(e) => setEditingPattern({ ...editingPattern, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">説明</label>
                <input
                  type="text"
                  value={editingPattern.description}
                  onChange={(e) => setEditingPattern({ ...editingPattern, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">開始時刻1</label>
                  <input
                    type="time"
                    value={editingPattern.start_time1}
                    onChange={(e) => setEditingPattern({ ...editingPattern, start_time1: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">終了時刻1</label>
                  <input
                    type="time"
                    value={editingPattern.end_time1}
                    onChange={(e) => setEditingPattern({ ...editingPattern, end_time1: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!editingPattern.start_time2}
                  onChange={(e) => setEditingPattern({
                    ...editingPattern,
                    start_time2: e.target.checked ? '17:00' : null,
                    end_time2: e.target.checked ? '21:00' : null
                  })}
                />
                <label className="text-sm">2つ目の時間帯を追加</label>
              </div>
              
              {editingPattern.start_time2 && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">開始時刻2</label>
                    <input
                      type="time"
                      value={editingPattern.start_time2 || ''}
                      onChange={(e) => setEditingPattern({ ...editingPattern, start_time2: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">終了時刻2</label>
                    <input
                      type="time"
                      value={editingPattern.end_time2 || ''}
                      onChange={(e) => setEditingPattern({ ...editingPattern, end_time2: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingPattern.break_required}
                  onChange={(e) => setEditingPattern({ ...editingPattern, break_required: e.target.checked })}
                />
                <label className="text-sm">中抜け必須</label>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">背景色</label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setEditingPattern({ ...editingPattern, background_color: color.value })}
                      className={`
                        p-3 rounded-lg border-2 transition-all
                        ${editingPattern.background_color === color.value ? 'border-blue-500 shadow-lg' : 'border-gray-200'}
                        ${color.preview}
                      `}
                      title={color.label}
                    >
                      <span className="text-xs">{color.label}</span>
                    </button>
                  ))}
                </div>
                {editingPattern.background_color && (
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">選択中: </span>
                    <span className={`px-2 py-1 rounded text-xs ${getColorClasses(editingPattern.background_color)}`}>
                      プレビュー
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingPattern(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                キャンセル
              </button>
              <button
                onClick={savePattern}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                <Save className="inline-block w-4 h-4 mr-2" />
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 編集モーダル - 時給ルール */}
      {editingRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              {editingRule.id ? 'ルール編集' : 'ルール追加'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">ルール名</label>
                <input
                  type="text"
                  value={editingRule.name}
                  onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">タイプ</label>
                <select
                  value={editingRule.type}
                  onChange={(e) => setEditingRule({ ...editingRule, type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="weekend">土日祝</option>
                  <option value="time">時間帯</option>
                  <option value="peak">繁忙期</option>
                  <option value="holiday">祝日</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">増額（円）</label>
                <input
                  type="number"
                  value={editingRule.amount}
                  onChange={(e) => setEditingRule({ ...editingRule, amount: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {editingRule.type === 'time' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">開始時刻</label>
                    <input
                      type="time"
                      value={editingRule.conditions.start_time || ''}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        conditions: { ...editingRule.conditions, start_time: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">終了時刻</label>
                    <input
                      type="time"
                      value={editingRule.conditions.end_time || ''}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        conditions: { ...editingRule.conditions, end_time: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
              
              {/* アイコン選択 */}
              <div>
                <label className="block text-sm font-medium mb-2">アイコン（カレンダーに表示）</label>
                <div className="grid grid-cols-7 gap-2">
                  {iconOptions.map((iconOpt) => {
                    const IconComponent = iconOpt.icon;
                    const isSelected = editingRule.icon === iconOpt.value;
                    return (
                      <button
                        key={iconOpt.value}
                        type="button"
                        onClick={() => setEditingRule({ 
                          ...editingRule, 
                          icon: iconOpt.value,
                          icon_color: editingRule.icon_color || iconOpt.defaultColor 
                        })}
                        className={`
                          p-2 rounded-lg border-2 transition-all
                          ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
                        `}
                        title={iconOpt.label}
                      >
                        <IconComponent className={`w-5 h-5 mx-auto ${isSelected ? iconOpt.defaultColor : 'text-gray-500'}`} />
                      </button>
                    );
                  })}
                </div>
                {editingRule.icon && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-600">選択中:</span>
                    {(() => {
                      const IconComponent = getIconComponent(editingRule.icon);
                      return IconComponent ? (
                        <div className="flex items-center gap-1">
                          <IconComponent className={`w-4 h-4 ${getIconColorClass(editingRule.icon_color, editingRule.icon)}`} />
                          <span className="text-sm">{iconOptions.find(opt => opt.value === editingRule.icon)?.label}</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingRule.is_active}
                  onChange={(e) => setEditingRule({ ...editingRule, is_active: e.target.checked })}
                />
                <label className="text-sm">有効</label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingRule(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                キャンセル
              </button>
              <button
                onClick={saveRule}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                <Save className="inline-block w-4 h-4 mr-2" />
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkPatternManager;